import { useEffect, useRef } from 'react';
import { usePlaybackStore } from '@/store/playbackStore.ts';
import { useProjectStore } from '@/store/projectStore.ts';
import { useUiStore } from '@/store/uiStore.ts';

/**
 * rAF-based playback loop.
 * Advances currentTime while isPlaying === true.
 * Respects shuttleSpeed for J/K/L shuttle controls.
 */
export function usePlayback() {
  const rafRef = useRef<number>(0);
  const lastFrameRef = useRef<number>(0);

  const isPlaying = usePlaybackStore(s => s.isPlaying);
  const currentTime = usePlaybackStore(s => s.currentTime);
  const shuttleSpeed = usePlaybackStore(s => s.shuttleSpeed);
  const seek = usePlaybackStore(s => s.seek);
  const pause = usePlaybackStore(s => s.pause);
  const getViewOffset = useProjectStore(s => s.getViewOffset);
  const getViewDuration = useProjectStore(s => s.getViewDuration);
  useProjectStore(s => s.activeSceneId); // re-render trigger for scene changes
  const getShotAtTime = useProjectStore(s => s.getShotAtTime);
  const selectShot = useProjectStore(s => s.selectShot);
  const reviewAutoPlay = useUiStore(s => s.reviewAutoPlay);

  const viewOffset = getViewOffset();
  const viewDuration = getViewDuration();
  const viewEndTime = viewOffset + viewDuration;

  // Store latest values in refs so the rAF callback doesn't go stale
  const stateRef = useRef({ currentTime, viewEndTime, viewOffset, shuttleSpeed, reviewAutoPlay });
  stateRef.current = { currentTime, viewEndTime, viewOffset, shuttleSpeed, reviewAutoPlay };

  useEffect(() => {
    if (!isPlaying) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }

    lastFrameRef.current = performance.now();

    const tick = (now: number) => {
      const dt = (now - lastFrameRef.current) / 1000;
      lastFrameRef.current = now;

      const { currentTime: ct, viewEndTime: vet, viewOffset: vo, shuttleSpeed: speed, reviewAutoPlay: autoP } = stateRef.current;
      const nextTime = ct + dt * speed;

      // Forward boundary
      if (nextTime >= vet) {
        seek(vet);
        pause();
        return;
      }

      // Reverse boundary (strict < so starting at exactly viewOffset doesn't pause)
      if (nextTime < vo) {
        seek(vo);
        pause();
        return;
      }

      // Auto-pause at shot boundaries
      if (autoP) {
        const curShot = getShotAtTime(ct);
        const nextShot = getShotAtTime(nextTime);
        if (curShot && nextShot && curShot.id !== nextShot.id) {
          // Skip auto-pause if both shots are in the same continuous chain
          const chain = useProjectStore.getState().getContinuousChain(curShot.id);
          const inSameChain = chain && chain.shots.some(s => s.id === nextShot.id);
          if (!inSameChain) {
            // Seek to the boundary (start of the new shot) and pause
            const boundaryTime = useProjectStore.getState().getShotStartTime(nextShot.id);
            seek(boundaryTime);
            selectShot(nextShot.id);
            pause();
            return;
          }
        }
      }

      seek(nextTime);
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isPlaying, seek, pause, getShotAtTime, selectShot]);
}
